import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import round from 'lodash/round';

import { Icon, Segment, Table } from 'semantic-ui-react';

import RefreshButton from './utils/refresh-button';
import { getStatus } from '../modules/status';

import '../stylesheets/status.css';

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;
const STATUS_UPDATE_INTERVAL_MS = 120000;
const PROCESSING_LOAD_THRESHOLD = 50;
const DIARIZATION_TIME = 360; // number of seconds to diarize a 1-hr 3-person meeting

export class StatusContainer extends Component {
  componentDidMount() {
    const { connectedGetStatus } = this.props;
    connectedGetStatus();
  }

  convertTime = (timeInt) => {
    if (timeInt > SECONDS_PER_HOUR) {
      return `${(timeInt / SECONDS_PER_HOUR).toFixed(1)} hours`;
    } if (timeInt > SECONDS_PER_MINUTE) {
      return `${(timeInt / SECONDS_PER_MINUTE).toFixed(1)} minutes`;
    } if (timeInt) {
      return `${timeInt} seconds`;
    }
    return `${0} seconds`;
  }

  calculate10MinEstWaitTime = () => {
    const { average } = this.props;
    return this.convertTime(
      round(60 * (10 / (average.processing_speed || 1))
      + (average.waiting_time || 0), 1),
    );
  }

  calculate1HrEstWaitTime = () => {
    const { average } = this.props;
    return this.convertTime(
      round(60 * (60 / (average.processing_speed || 1))
      + (average.waiting_time || 0) + DIARIZATION_TIME, 1),
    );
  }

  calculateStatusMessage = () => {
    const { current } = this.props;
    if (!current) return 'The system appears to be unavailable. Please contact support.';

    if (current.waiting_ratio === 0
      && current.processing_load < PROCESSING_LOAD_THRESHOLD) {
      return (
        <div className="re is-flex is-flex-align-baseline">
          <Icon color="green" name="check circle" size="large" />
          System running normally.
        </div>
      );
    } if (current.waiting_ratio) {
      return (
        <div className="re is-flex is-flex-align-baseline">
          <Icon color="yellow" name="exclamation circle" size="large" />
          Processing is slower than usual. New jobs must wait.
        </div>
      );
    }
    return (
      <div className="re is-flex is-flex-align-baseline">
        <Icon color="yellow" name="exclamation circle" size="large" />
        Processing is slower than usual.
      </div>
    );
  }

  renderUnavailableMessage = () => {
    const { navigator } = window;
    if (navigator.onLine) {
      return 'System down.';
    }
    return 'Your computer appears to be offline.';
  }

  render() {
    const {
      available,
      average,
      connectedGetStatus,
      current,
    } = this.props;
    return (
      <div className="re is-full-height is-main-top is-background-grey status-page">
        <Segment className="re is-padding-32px is-background-white status-card">
          {available
            ? (
              <div>
                <div className="re is-flex is-flex-row is-flex-justify-space-between">
                  <h3>System Status</h3>
                  <RefreshButton
                    callback={connectedGetStatus}
                    delay={STATUS_UPDATE_INTERVAL_MS}
                  />
                </div>
                { this.calculateStatusMessage() }
                <table className="ui basic status table">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell textAlign="right">Statistic</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">Value</Table.HeaderCell>
                      <Table.HeaderCell>Description</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell textAlign="right">Current processing load</Table.Cell>
                      <Table.Cell textAlign="center">
                        { current
                            && `${current.processing_load}%` }
                      </Table.Cell>
                      <Table.Cell>Total processor usage across entire system</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell textAlign="right">Current waiting ratio</Table.Cell>
                      <Table.Cell textAlign="center">
                        { current
                            && current.waiting_ratio.toFixed(1) }
                      </Table.Cell>
                      <Table.Cell>Number of jobs waiting over jobs processing</Table.Cell>
                    </Table.Row>
                    <Table.Row className="shaded">
                      <Table.Cell textAlign="right">Average processing speed</Table.Cell>
                      <Table.Cell textAlign="center">
                        { average && (average.processing_speed || 0).toFixed(1) }
                      </Table.Cell>
                      <Table.Cell>How much faster than real-time media playback</Table.Cell>
                    </Table.Row>
                    <Table.Row className="shaded">
                      <Table.Cell textAlign="right">Average processing time</Table.Cell>
                      <Table.Cell textAlign="center">
                        { average
                            && this.convertTime(average.processing_time) }
                      </Table.Cell>
                      <Table.Cell>Average over jobs completed in past hour</Table.Cell>
                    </Table.Row>
                    <Table.Row className="shaded">
                      <Table.Cell textAlign="right">Average waiting time</Table.Cell>
                      <Table.Cell textAlign="center">
                        { average
                            && this.convertTime(average.waiting_time) }
                      </Table.Cell>
                      <Table.Cell>Average over jobs started in past hour</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell textAlign="right">Estimate: 10min 2-channel call</Table.Cell>
                      <Table.Cell textAlign="center">{ this.calculate10MinEstWaitTime() }</Table.Cell>
                      <Table.Cell>Expected wait and processing time for new jobs</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell textAlign="right">Estimate: 1hr 3-person meeting</Table.Cell>
                      <Table.Cell textAlign="center">{ this.calculate1HrEstWaitTime() }</Table.Cell>
                      <Table.Cell>Includes additional speaker diarization overhead</Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </table>
              </div>
            )
            : (
              <div className="re is-flex is-flex-align-baseline">
                <Icon color="red" name="times circle" size="large" />
                { this.renderUnavailableMessage() }
                <RefreshButton
                  className="re is-marginLeft-quarterEm"
                  callback={connectedGetStatus}
                />
              </div>
            )
          }
        </Segment>
      </div>
      /* eslint-enable react/jsx-one-expression-per-line */
    );
  }
}

StatusContainer.propTypes = {
  available: PropTypes.bool.isRequired,
  average: PropTypes.shape({
    processing_speed: PropTypes.number,
    processing_time: PropTypes.number,
    waiting_time: PropTypes.number,
  }).isRequired,
  current: PropTypes.shape({
    processing_load: PropTypes.number,
    waiting_ratio: PropTypes.number,
  }).isRequired,
  connectedGetStatus: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  available: state.status.available,
  current: state.status.current,
  average: state.status.average,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  connectedGetStatus: getStatus,
}, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(StatusContainer);
